import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import Login from "./auth/LogIn.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import User from "./auth/ProfileForm.jsx";
import WelcomeFarmerScreen from "./screens/WelcomeFarmerScreen.jsx";
import WelcomeBuyerScreen from "./screens/WelcomeBuyerScreen.jsx";
import TestNewLayoutPage from "./pages/TestNewLayoutPage.jsx";
import MyListings from "./screens/CropListing/MyListings.jsx";
import Pricing from "./screens/CropListing/Pricing.jsx";
import CropCategory from "./screens/CropListing/CropCategory.jsx";
import Photos from "./screens/CropListing/Photos.jsx";
import LocationDelivery from "./screens/CropListing/LocationDelivery.jsx";
import CarbonCreditsDashboard from "./screens/CarbonCreditsScreen/CarbonCreditsDashboard.jsx";
import CarbonCreditsFarmDetails from "./screens/CarbonCreditsScreen/CarbonCreditsFarmDetails.jsx";
import CarbonCreditsPractices from "./screens/CarbonCreditsScreen/CarbonCreditsPractices.jsx";
import CarbonCreditsTimeline from "./screens/CarbonCreditsScreen/CarbonCreditsTimeline.jsx";
import CarbonCreditsReview from "./screens/CarbonCreditsScreen/CarbonCreditsReview.jsx";
import CarbonCreditsUploadProof from "./screens/CarbonCreditsScreen/CarbonCreditsUploadProof.jsx";
import CarbonCreditsMyWallet from "./screens/CarbonCreditsScreen/CarbonCreditsMyWallet.jsx";
import CarbonCreditsAllProjects from "./screens/CarbonCreditsScreen/CarbonCreditsAllProjects.jsx";
import CarbonCreditsMyProofs from "./screens/CarbonCreditsScreen/CarbonCreditsMyProofs.jsx";
import CarbonCreditsSellCredit from "./screens/CarbonCreditsScreen/CarbonCreditsSellCredit.jsx";
import Profile from "./screens/FarmerScreen/Profile.jsx";
import BoostYourListing from "./screens/FarmerScreen/BoostYourListing.jsx";
import BuyerBoost from "./BuyerScreens/BuyerBoost/BuyerBoost.jsx";
import CropDetails from "./screens/CropDetails/CropDetails.jsx";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Wishlist from "./pages/SidebarPages/Wishlist.jsx";
import AboutUs from "./pages/SidebarPages/AboutUs.jsx";
import Support from "./pages/SidebarPages/Support.jsx";
import Settings from "./pages/SidebarPages/Settings.jsx";
import PrivacyPolicy from "./pages/SidebarPages/PrivacyPolicy.jsx";
import TermsAndConditions from "./pages/SidebarPages/TermsAndConditions.jsx";
import RefundPolicy from "./pages/SidebarPages/RefundPolicy.jsx";
import ShippingPolicy from "./pages/SidebarPages/ShippingPolicy.jsx";
import CancellationPolicy from "./pages/SidebarPages/CancellationPolicy.jsx";
import FAQs from "./pages/SidebarPages/FAQs.jsx";
import PurchaseHistory from "./pages/SidebarPages/PurchaseHistory.jsx";
import ViewedPlots from "./pages/ViewedPlots.jsx";
import Search from "./pages/Search.jsx";

import { useLocation } from "react-router-dom";
import { UserProvider, useUser } from "./context/UserContext";
import MainLayout from "./components/Layout/MainLayout";

// ScrollToTop component to reset scroll on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AppContent = () => {
  const { userData, setUserData } = useUser();
  const navigate = useNavigate();

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/userform" element={<User />} />

        {/* Welcome Screens - Standalone */}
        <Route
          path="/farmer-success"
          element={<WelcomeFarmerScreen onGoHome={() => navigate("/home")} />}
        />
        <Route
          path="/buyer-success"
          element={<WelcomeBuyerScreen onGoHome={() => navigate("/home")} />}
        />
        <Route path="/test-layout" element={<TestNewLayoutPage />} />

        {/* Main Application Layout (Authenticated/Navigated Routes) */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/home" element={<Dashboard />} />

          {/* Admin Routes */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/manage-crops" element={<Dashboard />} />
          <Route path="/manage-plans" element={<Dashboard />} />
          <Route path="/free-plans" element={<Dashboard />} />
          <Route path="/manage-dashboard" element={<Dashboard />} />
          <Route path="/list-buyers" element={<Dashboard />} />
          <Route path="/list-sellers" element={<Dashboard />} />
          <Route path="/list-all-plots" element={<Dashboard />} />
          <Route path="/manage-carbon-proofs" element={<Dashboard />} />

          {/* Crop Listing Flow */}
          <Route path="/my-listings" element={<MyListings userData={userData} />} />
          <Route path="/pricing" element={<Pricing userData={userData} />} />
          <Route
            path="/add-crop-step-1"
            element={<CropCategory userData={userData} />}
          />
          <Route path="/photos" element={<Photos userData={userData} />} />
          <Route
            path="/location-delivery"
            element={<LocationDelivery userData={userData} />}
          />

          {/* Carbon Credits Flow */}
          <Route
            path="/carbon-credits"
            element={<CarbonCreditsDashboard userData={userData} />}
          />
          <Route
            path="/carbon-credits/farm-details"
            element={
              <CarbonCreditsFarmDetails
                userData={userData}
                onNext={() => navigate("/carbon-credits/practices")}
                onBack={() => navigate("/carbon-credits")}
              />
            }
          />
          <Route
            path="/carbon-credits/practices"
            element={
              <CarbonCreditsPractices
                userData={userData}
                onNext={() => navigate("/carbon-credits/timeline")}
                onBack={() => navigate("/carbon-credits/farm-details")}
              />
            }
          />
          <Route
            path="/carbon-credits/timeline"
            element={
              <CarbonCreditsTimeline
                userData={userData}
                onNext={() => navigate("/carbon-credits/review")}
                onBack={() => navigate("/carbon-credits/practices")}
              />
            }
          />
          <Route
            path="/carbon-credits/review"
            element={
              <CarbonCreditsReview
                userData={userData}
                onNext={() => navigate("/carbon-credits")}
                onBack={() => navigate("/carbon-credits/timeline")}
              />
            }
          />
          <Route
            path="/carbon-credits/upload-proof"
            element={
              <CarbonCreditsUploadProof
                userData={userData}
                onBack={() => navigate("/carbon-credits")}
              />
            }
          />
          <Route
            path="/carbon-credits/wallet"
            element={
              <CarbonCreditsMyWallet
                onBack={() => navigate("/carbon-credits")}
              />
            }
          />
          <Route
            path="/carbon-credits/all-projects"
            element={
              <CarbonCreditsAllProjects
                userData={userData}
                onBack={() => navigate("/carbon-credits")}
              />
            }
          />
          <Route
            path="/carbon-credits/my-proofs"
            element={
              <CarbonCreditsMyProofs
                userData={userData}
                onBack={() => navigate("/carbon-credits")}
                onUploadProof={() => navigate("/carbon-credits/upload-proof")}
              />
            }
          />
          <Route
            path="/carbon-credits/sell-credit"
            element={
              <CarbonCreditsSellCredit
                onBack={() => navigate("/carbon-credits")}
              />
            }
          />

          {/* User Specific Screens */}
          {/* test comment */}
          <Route path="/farmer/boost" element={<BoostYourListing />} />
          <Route path="/profile" element={<Profile userData={userData} />} />
          <Route
            path="/farmer/profile"
            element={<Profile userData={userData} />}
          />
          <Route
            path="/buyer/boost"
            element={<BuyerBoost />}
          />
          <Route
            path="/crop/:id"
            element={<CropDetails userData={userData} />}
          />
          <Route path="/wishlist" element={<Wishlist userData={userData} setUserData={setUserData} />} />
          <Route path="/about" element={<AboutUs />} />
          <Route
            path="/support"
            element={<Support faqsPage={() => navigate("/faqs")} />}
          />
          <Route
            path="/settings"
            element={<Settings isUserID={userData?.uid} />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route
            path="/purchase-history"
            element={<PurchaseHistory userData={userData} />}
          />
          <Route
            path="/viewed-plots"
            element={<ViewedPlots userData={userData} />}
          />
          <Route
            path="/explore"
            element={
              <Search
                userData={userData}
                role={userData?.role}
                setUserData={setUserData}
              />
            }
          />
        </Route>
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
    </>
  );
};

const App = () => {
  return (
    <Router>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </Router>
  );
};

export default App;
